import {useEffect, useRef, useState} from 'react'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {useTranslation} from '@/translations/i18n'
import {MedicalReportValidationSchema} from '@/features/medical-reports/formSchemas'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {Text} from '@/components/ui/text/Text'
import {
    SelectComboboxInput,
    SelectComboboxPopover,
    SelectItem,
    SelectList,
    SelectTrigger
} from '@/components/ui/select-atoms/SelectAtoms'
import {InputSelectUser} from '@/features/users/components/input-select-user/InputSelectUser'

/**
 * Admin-only selector shown under the "Firma operatore" checkbox in the report confirmation dialog.
 * Lets an Admin choose whose signature to apply:
 * - when the report is linked to a project (PRI): a member of that project's team (equipe),
 *   read directly from the already-loaded `project.team`;
 * - otherwise: any operative user of the platform (via `InputSelectUser`).
 *
 * Writes the choice into the RHF `signedForUser` field. Leaving it empty signs as the current user.
 */
export const MedicalReportSignerField = () => {
    const {t} = useTranslation()
    const form = useFormContext<MedicalReportValidationSchema>()
    const project = useWatch({control: form.control, name: 'project'})
    const noProject = useWatch({control: form.control, name: 'noProject'})
    const accesses = useWatch({control: form.control, name: 'accesses'})
    const signedForUser = useWatch({control: form.control, name: 'signedForUser'})
    const [searchValue, setSearchValue] = useState('')

    // Pre-select the consultant who performed the report's associated access, once, as long as no
    // signer has been chosen yet — this is only a default and never overrides an explicit selection.
    const hasPrefilledSignerRef = useRef(false)
    useEffect(() => {
        if (hasPrefilledSignerRef.current) {
            return
        }
        if (signedForUser) {
            hasPrefilledSignerRef.current = true
            return
        }
        const accessConsultant = accesses?.map(access => access.consultant).find(Boolean)
        if (accessConsultant) {
            form.setValue('signedForUser', {id: accessConsultant.id, fullName: accessConsultant.fullName})
            hasPrefilledSignerRef.current = true
        }
    }, [accesses, signedForUser, form])

    const isProjectAssociated = !noProject && !!project?.id
    const projectTeam = project?.team ?? []

    const filteredTeam = projectTeam.filter(
        member =>
            member.fullName.toLowerCase().includes(searchValue.toLowerCase()) ||
            member.specializationName?.toLowerCase().includes(searchValue.toLowerCase())
    )

    return (
        <Controller
            control={form.control}
            name="signedForUser"
            render={({field}) => (
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Label>{t('medical_records:sign_on_behalf')}</Label>
                    {isProjectAssociated ? (
                        <ComboboxProvider setValue={setSearchValue} resetValueOnHide>
                            <SelectProvider
                                value={field.value?.id ?? ''}
                                setValue={(id: string) => {
                                    const member = projectTeam.find(teamMember => teamMember.id === id)
                                    field.onChange(member ? {id: member.id, fullName: member.fullName} : null)
                                }}
                            >
                                <SelectTrigger
                                    fullWidth
                                    placeholder={t('table_filters:select_operator')}
                                    displayValue={() => field.value?.fullName ?? ''}
                                />
                                <SelectComboboxPopover portal sameWidth>
                                    <SelectComboboxInput placeholder={t('commons:search_operator')} />
                                    {filteredTeam.length > 0 ? (
                                        <SelectList>
                                            {filteredTeam.map(member => (
                                                <SelectItem key={member.id} value={member.id}>
                                                    <Flexbox gap={1}>
                                                        {member.fullName}
                                                        <Text size="textSm" palette="neutral" shade="600">
                                                            ({member.specializationName})
                                                        </Text>
                                                    </Flexbox>
                                                </SelectItem>
                                            ))}
                                        </SelectList>
                                    ) : (
                                        <SelectItem disabled>{t('medical_records:no_operators_present')}</SelectItem>
                                    )}
                                </SelectComboboxPopover>
                            </SelectProvider>
                        </ComboboxProvider>
                    ) : (
                        <InputSelectUser
                            value={field.value ?? null}
                            onChange={user => field.onChange(user ? {id: user.id, fullName: user.fullName} : null)}
                            onClear={() => field.onChange(null)}
                            placeholder={t('table_filters:select_operator')}
                            filters={{status: 'operative'}}
                            portal
                        />
                    )}
                </Flexbox>
            )}
        />
    )
}
